import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { FormField, Modal, SortableTableHead, Textarea, PAGE } from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import { useTimezone } from '@kioskgaming/ui';
import { formatInTimezone } from '@kioskgaming/ui';
import PortalEmptyState from '../components/ui/PortalEmptyState';
import WithdrawalStatusBadge from '../components/withdrawals/WithdrawalStatusBadge';
import { getTableSortColumns } from '../config/tableSortConfig';
import useTableSort from '../hooks/useTableSort';
import { approveShopWithdrawal, fetchShopWithdrawals, rejectShopWithdrawal } from '../services/api';
import type { ShopWithdrawalRequest } from '../types';
import { getShopWithdrawalReviewReason } from '../utils/shopWithdrawalReviewReason';

const PAGE_SIZE = 200;

function formatUsd(amount: number) {
  return `$${amount.toFixed(2)}`;
}

export default function WithdrawalsApprovalPage() {
  const { timezone } = useTimezone();
  const [searchParams] = useSearchParams();
  const page = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10) || 1);

  const tableSort = useTableSort({
    columns: getTableSortColumns('shopWithdrawals'),
  });

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ShopWithdrawalRequest[]>([]);
  const [threshold, setThreshold] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<ShopWithdrawalRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchShopWithdrawals({
        page,
        limit: PAGE_SIZE,
        ...tableSort.sortParams,
      });
      if (res.success) {
        const w = res.data?.withdrawals ?? [];
        setRows(w);
        setThreshold(typeof res.data?.threshold === 'number' ? res.data.threshold : null);
      } else {
        toast.error(res.message || 'Failed to load withdrawals');
        setRows([]);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to load withdrawals');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, tableSort.sortBy, tableSort.sortOrder, tableSort.sortParams]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const phone = String(r.user?.phone || '').toLowerCase();
      const email = String(r.user?.email || '').toLowerCase();
      const tid = String(r.transactionId || '').toLowerCase();
      return `${phone} ${email} ${tid}`.includes(q);
    });
  }, [rows, query]);

  const headerSort = {
    activeKey: tableSort.sortBy,
    direction: tableSort.sortOrder,
  };

  const isTableBusy = loading;

  const approve = async (w: ShopWithdrawalRequest) => {
    if (!w?.transactionId) return;
    setApprovingId(w.transactionId);
    try {
      const res = await approveShopWithdrawal({ transactionId: w.transactionId });
      if (res.success) {
        toast.success('Approved successfully');
        await load();
      } else {
        toast.error(res.message || 'Approve failed');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Approve failed');
      await load();
    } finally {
      setApprovingId(null);
    }
  };

  const openRejectModal = (w: ShopWithdrawalRequest) => {
    setRejectTarget(w);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    if (rejectingId) return;
    setRejectModalOpen(false);
    setRejectTarget(null);
    setRejectReason('');
  };

  const submitReject = async () => {
    const w = rejectTarget;
    if (!w?.transactionId) return;
    const reason = rejectReason.trim();
    if (!reason) {
      toast.error('Please enter a rejection reason');
      return;
    }

    setRejectingId(w.transactionId);
    try {
      const res = await rejectShopWithdrawal({ transactionId: w.transactionId, reason });
      if (res.success) {
        toast.success('Rejected successfully');
        closeRejectModal();
        await load();
      } else {
        toast.error(res.message || 'Reject failed');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Reject failed');
    } finally {
      setRejectingId(null);
    }
  };

  return (
    <AppLayout contentFullWidth>
      <div className="mb-6 space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{PAGE.withdrawalRequests}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Approve pending withdrawals for your Shop Players
              {threshold != null ? (
                <span className="text-gray-400">
                  {' '}
                  (auto-approve normally applies below {threshold.toFixed(2)} USD; pending below that still need your review)
                </span>
              ) : null}
              .
            </p>
          </div>
          <div className="flex gap-2">
            <input
              type="search"
              placeholder="Search phone, email, transaction…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none sm:w-80"
            />
            <button
              type="button"
              onClick={() => void load()}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <PageLoadingIndicator variant="page" accentColor="#059669" />
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-emerald-100 bg-white shadow-sm">
          <PortalEmptyState
            icon="✅"
            title="No withdrawals"
            description="There are no withdrawal requests for your Shop Players yet."
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-emerald-100 bg-white shadow-sm">
          <div className="overflow-x-auto" aria-busy={isTableBusy || undefined}>
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-gray-100 bg-gray-50/80 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <SortableTableHead
                    sortable
                    label="Time"
                    sortKey="createdAt"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                    className="px-4 py-3 normal-case"
                  />
                  <th className="px-4 py-3">Player</th>
                  <th className="px-4 py-3">Transaction</th>
                  <SortableTableHead
                    sortable
                    label="Amount"
                    sortKey="amount"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    align="right"
                    disabled={isTableBusy}
                    className="px-4 py-3 normal-case"
                  />
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Review reason</th>
                  <SortableTableHead
                    sortable
                    label="Status"
                    sortKey="status"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                    className="px-4 py-3 normal-case"
                  />
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((w) => {
                  const created = w.createdAt ? formatInTimezone(w.createdAt, timezone) : '—';
                  const player = w.user?.phone || w.user?.email || w.userId;
                  const approving = approvingId === w.transactionId;
                  const rejecting = rejectingId === w.transactionId;
                  const canAct = w.status === 'pending';
                  return (
                    <tr key={w.transactionId} className="hover:bg-emerald-50/30">
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">{created}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{player || '—'}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">{w.transactionId}</td>
                      <td className="px-4 py-3 text-right font-semibold text-amber-800 tabular-nums">
                        {formatUsd(w.amount)}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{w.withdrawalType || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-[16rem]">
                        {getShopWithdrawalReviewReason(w) || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <WithdrawalStatusBadge status={w.status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canAct ? (
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              disabled={approving || rejecting}
                              onClick={() => openRejectModal(w)}
                              className="rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
                            >
                              {rejecting ? 'Rejecting…' : 'Reject'}
                            </button>
                            <button
                              type="button"
                              disabled={approving || rejecting}
                              onClick={() => void approve(w)}
                              className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                            >
                              {approving ? 'Approving…' : 'Approve'}
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={rejectModalOpen}
        onClose={closeRejectModal}
        title="Reject withdrawal"
        subtitle="Please enter a rejection reason."
        variant="form"
        size="sm"
        primaryAction={{
          label: 'Reject',
          onClick: () => void submitReject(),
          loading: !!rejectingId,
          disabled: !!rejectingId || !rejectTarget?.transactionId || !rejectReason.trim(),
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: closeRejectModal,
        }}
        closeOnOverlayClick={!rejectingId}
        preventClose={!!rejectingId}
      >
        <div className="space-y-4">
          {rejectTarget ? (
            <div className="rounded-xl border border-stone-200 bg-white p-3 text-sm text-stone-700">
              <div className="text-xs text-stone-500">Transaction</div>
              <div className="mt-1 break-all font-mono text-xs text-stone-900">{rejectTarget.transactionId}</div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-stone-50 px-2 py-2">
                  <div className="text-stone-500">Player</div>
                  <div className="mt-0.5 font-semibold text-stone-900">
                    {rejectTarget.user?.phone || rejectTarget.user?.email || rejectTarget.userId || '—'}
                  </div>
                </div>
                <div className="rounded-lg bg-stone-50 px-2 py-2 text-right">
                  <div className="text-stone-500">Amount</div>
                  <div className="mt-0.5 font-semibold tabular-nums text-stone-900">
                    {formatUsd(rejectTarget.amount)}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <FormField label="Reason" required state={rejectingId ? 'disabled' : 'default'}>
            <Textarea
              placeholder="Example: Invalid withdrawal details / suspected fraud / wrong account…"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              disabled={!!rejectingId}
              rows={4}
            />
          </FormField>
        </div>
      </Modal>
    </AppLayout>
  );
}
